import type { CaptureResult, PostHogConfig } from "posthog-js"
import { env } from "../../config"
import { THIRD_PARTY_SCRIPT_ERROR_PATTERN } from "../../constants/third-party-errors"

type ExceptionFrame = {
  filename?: string
}

type ExceptionEntry = {
  type?: string
  value?: string
  stacktrace?: {
    frames?: ExceptionFrame[]
  }
}

// The PostHog free tier allows 100k exceptions/month across every environment.
// The SDK rate limiter is per exception type with no overall cap, so a page
// that throws many distinct errors needs its own ceiling. The cap is per full
// document load: it deliberately survives client-side navigations, so one tab
// sends at most this many exceptions until a hard reload.
export const MAX_EXCEPTIONS_PER_PAGE_LOAD = 10

// Browser-generated errors with no actionable first-party cause: opaque
// cross-origin script errors, the benign ResizeObserver loop warning, and
// scripts some in-app browsers inject inline into the page, so their frames
// look first-party: crypto-wallet shims (e.g. Brave iOS) and the Google
// Search app's `gmo` global. The `gmo` alternatives are anchored so a
// first-party identifier that merely starts with `gmo` is still reported.
// Microsoft's email link scanner (Outlook Safe Links, a headless CefSharp
// browser) rejects with a bare `Object Not Found Matching Id:<n>, …` string;
// it is matched by its full shape, not the "Object Not Found" phrase.
// The Next.js same-URL invariant fires when the router's hydration-time
// `_next/data` refetch fails (e.g. a mobile visitor leaves mid-request); the
// router throws instead of reloading, and the SSR page is already usable.
// A Wildfire-powered cashback extension redirects visitors through its
// affiliate link: its aborted request rejects with a non-Error `AxiosError`
// (our axios 0.21 has no such class) and its message bridge throws
// `user:action getUserId not found`. Both are matched by their exact text.
// The bridge also rejects with a non-Error object carrying the same message,
// which PostHog wraps as `'InvalidParametersError' captured as exception …`.
// Safari throws `Invalid call to runtime.sendMessage(). Tab not found.` when an
// extension's content script messages from a page not attached to a tab (e.g.
// an address-bar preload); it has no frames and is matched by its exact text.
const BROWSER_NOISE_PATTERN = /^Script error\.?$|ResizeObserver loop|window\.ethereum|^Can't find variable: gmo$|^gmo is not defined$|Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+|^Invariant: attempted to hard navigate to the same URL |^'AxiosError' captured as exception with message: 'Network Error'$|^user:action getUserId not found$|^'InvalidParametersError' captured as exception with message: 'user:action getUserId not found'$|^Invalid call to runtime\.sendMessage\(\)\. Tab not found\.$/i

let sentExceptionCount = 0

export function isErrorTrackingEnabled(): boolean {
  return env("FF_ERROR_TRACKING_ENABLED") === "1"
}

function isThirdPartyFrame(frame: ExceptionFrame): boolean {
  if (!frame.filename) {
    return false
  }

  try {
    return new URL(frame.filename).origin !== window.location.origin
  } catch {
    // Non-URL filenames such as `<anonymous>` may be first-party; keep them.
    return false
  }
}

export function shouldDropException(event: CaptureResult): boolean {
  const exceptionList = event.properties.$exception_list as
    | ExceptionEntry[]
    | undefined
  const exception = exceptionList?.[0]

  if (!exception) {
    return false
  }

  const message = `${exception.type ?? ""}: ${exception.value ?? ""}`
  const isVendorOrBrowserNoise =
    THIRD_PARTY_SCRIPT_ERROR_PATTERN.test(message) ||
    BROWSER_NOISE_PATTERN.test(exception.value ?? "")

  if (isVendorOrBrowserNoise) {
    return true
  }

  const frames = exception.stacktrace?.frames ?? []

  return frames.length > 0 && frames.every(isThirdPartyFrame)
}

function filterExceptions(event: CaptureResult | null): CaptureResult | null {
  if (!event || event.event !== "$exception") {
    return event
  }

  if (shouldDropException(event)) {
    return null
  }

  if (sentExceptionCount >= MAX_EXCEPTIONS_PER_PAGE_LOAD) {
    return null
  }

  sentExceptionCount += 1

  return event
}

export function resetExceptionCountForTests(): void {
  sentExceptionCount = 0
}

/**
 * Exception options for every `posthog.init`. When the kill switch is off,
 * `capture_exceptions: false` is set explicitly so the project-level remote
 * "exception autocapture" setting cannot turn capture back on.
 */
export function getExceptionCaptureOptions(): Partial<PostHogConfig> {
  if (!isErrorTrackingEnabled()) {
    return { capture_exceptions: false }
  }

  return {
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    error_tracking: {
      exceptionRateLimiterBucketSize: 5,
      exceptionRateLimiterRefillRate: 1,
    },
    before_send: filterExceptions,
  }
}
